import type { StoreResponse } from '@clarity/contract';
import { isNotYetOnboarded } from '../../lib/lifecycle.js';
import { useI18n } from '../../lib/i18n.js';

/**
 * Small shared helpers for the combined (All / Brand) flexible surfaces
 * (clarity-navigation-scope task 6, Req 6.1–6.4). Extracted so
 * {@link CombinedOrders}, {@link CombinedStatements} and {@link CombinedPayouts}
 * share the un-onboarded split + the "N still onboarding" note rather than each
 * re-deriving it (design §7 — "consider extracting a tiny shared helper").
 */

/** The translator shape from {@link useI18n}, narrowed for the helpers below. */
type Translate = ReturnType<typeof useI18n>['t'];

/**
 * Split an in-scope branch set into the ONBOARDED branches that contribute data
 * reads (Req 6.2) and a count of those excluded because they are not yet
 * onboarded. An un-onboarded branch has no persisted statements/payouts/orders
 * and its read would be rejected `409`, so it contributes nothing and is only
 * surfaced as a count via {@link OnboardingNote}.
 */
export function splitOnboarded(branches: StoreResponse[]): {
  onboarded: StoreResponse[];
  stillOnboarding: number;
} {
  const onboarded = branches.filter((b) => !isNotYetOnboarded(b));
  return { onboarded, stillOnboarding: branches.length - onboarded.length };
}

/**
 * The "N still onboarding" note (Req 6.2): how many in-scope branches are
 * excluded from the combined reads because they are not yet onboarded. Uses the
 * bilingual `scope.combined.onboarding.*` keys (one/many) with `{count}`.
 */
export function OnboardingNote({ count, t }: { count: number; t: Translate }) {
  const key =
    count === 1 ? 'scope.combined.onboarding.one' : 'scope.combined.onboarding.many';
  return (
    <p className="combined-view__onboarding-note muted" role="note">
      {t(key, { count })}
    </p>
  );
}
